import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Building2, Users, ShieldCheck, RefreshCw, ArrowLeft, ChevronRight, Mail } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Card, Empty } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { sdx500Api } from '../../api/sdx500.js';

const STATUS_LABEL = {
    active: { label: '使用中', cls: 'bg-success-100 text-[#3f5733]' },
    provisioned: { label: '待註冊', cls: 'bg-accent-50 text-accent-700' },
    awaiting_provision: { label: '待開通', cls: 'bg-warn-100 text-[#8a5e16]' },
    suspended: { label: '停用', cls: 'bg-danger-100 text-[#7a2c27]' },
};

export default function DistributorsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(null);

    async function load() {
        setLoading(true); setError(null);
        try { setData(await sdx500Api.distributorsOverview()); }
        catch (e) { setError(e.body?.hint || e.body?.error || e.message); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    return (
        <div>
            <Link to="/sdx500" className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-accent-700 transition mb-3">
                <ArrowLeft size={13} /> 返回授權管理
            </Link>
            <PageHeader
                eyebrow="TECOM SDX500 · super admin"
                title="東訊大盤商"
                description="東訊各大盤商（經銷商）與底下客戶、授權數。大盤商即授權歸屬 / 分潤對象；客戶帳號 createdBy 掛在對應大盤商下。"
                actions={<Button variant="outline" onClick={load} disabled={loading}><RefreshCw size={14} /> 重新整理</Button>}
            />

            {/* 總計 */}
            {data?.totals && (
                <div className="grid grid-cols-3 gap-3 mb-6">
                    {[
                        { label: '大盤商', value: data.totals.distributors, icon: ShieldCheck },
                        { label: '客戶帳號', value: data.totals.customers, icon: Users },
                        { label: '授權（台）', value: data.totals.licenses, icon: Building2 },
                    ].map((s) => (
                        <div key={s.label} className="bg-white border border-cream-200 rounded-lg p-4">
                            <div className="heading-display text-2xl font-semibold text-ink-900 leading-none">{s.value}</div>
                            <div className="text-xs text-ink-400 mt-2 flex items-center gap-1.5"><s.icon size={13} className="text-ink-400" />{s.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <Card><div className="text-sm text-ink-400 italic text-center py-10 flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />載入中…</div></Card>
            ) : error ? (
                <Card className="bg-danger-100/40 border-danger-500/30"><div className="text-sm text-danger-500">{error}</div></Card>
            ) : !data?.items?.length ? (
                <Card><Empty icon={ShieldCheck} title="尚無大盤商" description="用 tecom-update-distributors 匯入大盤商名單。" /></Card>
            ) : (
                <Card padded={false}>
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-ink-400 font-mono border-b border-cream-200 bg-cream-50">
                                <th className="w-8" />
                                <th className="text-left px-4 py-3 font-medium w-10">#</th>
                                <th className="text-left px-4 py-3 font-medium">大盤商</th>
                                <th className="text-right px-4 py-3 font-medium">客戶</th>
                                <th className="text-right px-4 py-3 font-medium">授權（台）</th>
                                <th className="text-left px-4 py-3 font-medium pl-6">狀態分佈</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((d, i) => {
                                const canExpand = d.licenseTotal > 0;
                                const isOpen = expanded === d.uid;
                                return (
                                    <>
                                        <tr key={d.uid}
                                            className={['border-b border-cream-100', canExpand ? 'hover:bg-cream-50 cursor-pointer' : ''].join(' ')}
                                            onClick={() => canExpand && setExpanded(isOpen ? null : d.uid)}>
                                            <td className="pl-4">
                                                {canExpand && <ChevronRight size={14} className={['text-ink-300 transition', isOpen ? 'rotate-90' : ''].join(' ')} />}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-[11px] text-ink-300">{i + 1}</td>
                                            <td className="px-4 py-3">
                                                <div className="text-ink-900 text-sm font-medium">{d.name}</div>
                                                <div className="font-mono text-[10px] text-ink-300">{d.email}</div>
                                            </td>
                                            <td className="px-4 py-3 text-right"><span className={['font-mono text-sm', d.customerCount ? 'text-ink-900' : 'text-ink-300'].join(' ')}>{d.customerCount}</span></td>
                                            <td className="px-4 py-3 text-right"><span className={['font-mono text-sm', d.licenseTotal ? 'text-ink-900' : 'text-ink-300'].join(' ')}>{d.licenseTotal}</span></td>
                                            <td className="px-4 py-3 pl-6">
                                                {d.licenseTotal === 0 ? <span className="text-ink-300 text-xs">—</span> : (
                                                    <span className="inline-flex flex-wrap gap-1.5">
                                                        {d.active > 0 && <Pill cls="bg-success-100 text-[#3f5733]">使用中 {d.active}</Pill>}
                                                        {d.provisioned > 0 && <Pill cls="bg-accent-50 text-accent-700">待註冊 {d.provisioned}</Pill>}
                                                        {d.awaiting > 0 && <Pill cls="bg-warn-100 text-[#8a5e16]">待開通 {d.awaiting}</Pill>}
                                                        {d.suspended > 0 && <Pill cls="bg-danger-100 text-[#7a2c27]">停用 {d.suspended}</Pill>}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="bg-cream-50 border-b border-cream-100">
                                                <td colSpan={6} className="px-6 py-3">
                                                    <div className="text-[10px] uppercase tracking-wider text-ink-400 font-mono mb-2">設備 / 商用帳號（{d.licenses.length}）</div>
                                                    <div className="space-y-1.5">
                                                        {d.licenses.map((l) => {
                                                            const st = STATUS_LABEL[l.status] || STATUS_LABEL.awaiting_provision;
                                                            return (
                                                                <div key={l.macAddress} className="flex items-center gap-3 bg-white border border-cream-200 rounded-lg px-3.5 py-2">
                                                                    <span className="font-mono text-[12px] text-ink-900 font-medium">{l.macAddress}</span>
                                                                    <span className={['inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium', st.cls].join(' ')}>{st.label}</span>
                                                                    <span className="flex-1" />
                                                                    {l.company || l.ownerEmail ? (
                                                                        <span className="flex items-center gap-2 text-[12.5px] text-ink-700">
                                                                            {l.company && <span className="font-medium">{l.company}</span>}
                                                                            {l.ownerEmail && <span className="inline-flex items-center gap-1 text-ink-400"><Mail size={12} />{l.ownerEmail}</span>}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-[12px] text-ink-300">{l.hasAccount ? '帳號資料待補' : '尚未建立客戶帳號'}</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}

function Pill({ cls, children }) {
    return <span className={['inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium', cls].join(' ')}>{children}</span>;
}
